import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Polyline,
  InfoWindow,
} from "@react-google-maps/api";
import {
  Box,
  Card,
  Typography,
  Chip,
  Switch,
  Stack,
  CircularProgress,
} from "@mui/material";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import env from "../../../env.json";
import TrackingServices from "../../services/externalServices/trackingservices/trackingServices";
import { getOwnerIdFromLocalStorage } from "../../utils/auth";

const containerStyle = {
  width: "100%",
  height: "520px",
};

const libraries = ["places", "geometry"];
const { GOOGLE_MAP_KEY } = env;

const DEFAULT_CENTER = { lat: 15.335, lng: 76.46 };

const BusLocation = () => {
  const { busId } = useParams();
  const mapRef = useRef(null);

  const [busLocation, setBusLocation] = useState(null);
  const [routeData, setRouteData] = useState({
    plannedPath: [],
    travelledPath: [],
    stops: [],
  });
  const [currentStop, setCurrentStop] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoFollow, setAutoFollow] = useState(true);
  const [ownerId, setOwnerId] = useState(null);
  const [tripType, setTripType] = useState("pickup");
  const [dateFilter, setDateFilter] = useState("today");
  const [selectedDate, setSelectedDate] = useState("");
  const [noData, setNoData] = useState(false);
  const [routeName, setRouteName] = useState("");

  // const { isLoaded } = useJsApiLoader({
  //   googleMapsApiKey: GOOGLE_MAP_KEY,
  //   libraries,
  // });

  /* ---------------------------
     Load ownerId
  --------------------------- */
  useEffect(() => {
    const id = getOwnerIdFromLocalStorage();
    if (id) setOwnerId(id);
    else setLoading(false);
  }, []);

  /* ---------------------------
     Resolve Date
  --------------------------- */
  const resolveDate = () => {
    const now = new Date();

    console.log("dateFilter", dateFilter);

    if (dateFilter === "today") return now.toISOString().split("T")[0];

    if (dateFilter === "yesterday") {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      return y.toISOString().split("T")[0];
    }

    if (dateFilter === "custom" && selectedDate) return selectedDate;

    return null;
  };

  /* ---------------------------
     Fetch Tracking
  --------------------------- */
  const fetchLatestLocation = async () => {
    if (!ownerId) return;

    try {
      setLoading(true);

      const res = await TrackingServices.getTrackingHistory({
        ownerId,
        busId,
        tripType: tripType === "all" ? undefined : tripType,
        date: resolveDate(),
      });

      const record = res?.data?.[0];

      if (!record) {
        setNoData(true);
        setBusLocation(null);
        setRouteData({ plannedPath: [], travelledPath: [], stops: [] });
        setCurrentStop(null);
        setLoading(false);
        return;
      }
      console.log("record",record.vehicleRoute_id)
      setRouteName(record.vehicleRoute_id?.route_name |"");
      setNoData(false);

      const latestLoc = record.location;
      setBusLocation(latestLoc);

      const plannedPath =
        record.vehicleRoute_id?.segments?.map((s) => ({
          lat: s.latitude,
          lng: s.longitude,
        })) || [];

      const travelledPath =
        record.pathCoordinates?.map((p) => ({
          lat: p.latitude,
          lng: p.longitude,
        })) || [];

      setRouteData({
        plannedPath,
        travelledPath,
        stops: record.route || [],
      });

      /* -------- Nearest Stop -------- */
      if (window.google?.maps?.geometry && record.route?.length) {
        let nearest = null;
        let minDist = Infinity;

        record.route.forEach((stop) => {
          const dist =
            window.google.maps.geometry.spherical.computeDistanceBetween(
              new window.google.maps.LatLng(latestLoc.lat, latestLoc.lng),
              new window.google.maps.LatLng(stop.latitude, stop.longitude),
            );
          if (dist < minDist) {
            minDist = dist;
            nearest = stop;
          }
        });

        setCurrentStop(nearest);
      }

      if (autoFollow && mapRef.current && latestLoc) {
        mapRef.current.panTo(latestLoc);
      }

      setLoading(false);
    } catch (err) {
      console.error(err);
      setNoData(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLatestLocation();
    const interval = setInterval(fetchLatestLocation, 30000);
    return () => clearInterval(interval);
  }, [busId, ownerId, tripType, dateFilter, selectedDate, autoFollow]);

  // if (!isLoaded || loading) {
  //   return (
  //     <Box height="60vh" display="flex" justifyContent="center" alignItems="center">
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  return (
    <Box p={2} sx={{ width: "calc(100vw - 260px)" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <DirectionsBusIcon color="primary" />
          <Box>
            <Typography fontWeight={600}>Bus Location</Typography>

            {/* ✅ Route Name */}
            <Typography variant="caption" color="text.secondary">
              Route: {routeName}
            </Typography>
          </Box>
          <Chip label="LIVE" color="success" size="small" />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption">Auto Refsefresh</Typography>
          <Switch
            checked={autoFollow}
            onChange={() => setAutoFollow(!autoFollow)}
          />
        </Stack>
      </Box>

      {noData && (
        <Box
          position="absolute"
          inset={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255,255,255,0.85)"
          zIndex={10}
        >
          <Typography fontWeight={600}>
            No tracking data found / Tracking not started
          </Typography>
        </Box>
      )}

      <Card sx={{ p: 2, mb: 2, overflowX: "auto" }}>
        <Stack direction="row" spacing={2}>
          {routeData.stops.map((stop) => (
            <Box
              key={stop._id}
              minWidth={220}
              p={1.5}
              borderRadius={2}
              border="1px solid"
              borderColor={
                stop._id === currentStop?._id ? "primary.main" : "divider"
              }
            >
              <Typography fontWeight={600} fontSize={14}>
                {stop.stopName}
              </Typography>
              <Typography variant="caption">
                Planned: {stop.plannedArrivalTime}
              </Typography>
              <br />
              <Typography variant="caption">
                Actual: {stop.actualArrivalTime || "--"}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Card>

      {/* Filters */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2}>
          <Box>
            <Typography variant="caption">Trip Type</Typography>
            <select
              value={tripType}
              onChange={(e) => setTripType(e.target.value)}
            >
              <option value="pickup">Pickup</option>
              <option value="drop">Drop</option>
              <option value="all">All</option>
            </select>
          </Box>

          <Box>
            <Typography variant="caption">Date</Typography>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="custom">Select Date</option>
            </select>
          </Box>

          {dateFilter === "custom" && (
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          )}
        </Stack>
      </Card>

      {/* Map */}
      <Card sx={{ p: 2, position: "relative" }}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={busLocation || routeData.plannedPath[0] || DEFAULT_CENTER}
          zoom={13}
          onLoad={(map) => (mapRef.current = map)}
        >
          {/* Planned Route */}
          {routeData.plannedPath.length > 0 && (
            <Polyline
              path={routeData.plannedPath}
              options={{ strokeWeight: 5 }}
            />
          )}

          {/* Travelled Path */}
          {routeData.travelledPath.length > 0 && (
            <Polyline
              path={routeData.travelledPath}
              options={{ strokeWeight: 4 }}
            />
          )}

          {/* Bus Marker */}
          {busLocation && (
            <Marker
              position={busLocation}
              icon={{
                url: "https://img.icons8.com/color/48/bus.png",
                scaledSize: new window.google.maps.Size(36, 36),
              }}
            />
          )}

          {/* Stop Markers */}
          {routeData.stops.map((stop) => {
            const stopPosition = {
              lat: stop.latitude,
              lng: stop.longitude,
            };

            return (
              <Marker
                key={stop._id}
                position={stopPosition}
                onClick={() => setActiveMarker(stop._id)}
                icon={{
                  url:
                    stop._id === currentStop?._id
                      ? "https://maps.google.com/mapfiles/ms/icons/green-dot.png"
                      : "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                }}
              >
                {activeMarker === stop._id && (
                  <InfoWindow
                    position={stopPosition} // ✅ THIS FIXES THE ERROR
                    onCloseClick={() => setActiveMarker(null)}
                  >
                    <Box>
                      <Typography fontWeight={600}>{stop.stopName}</Typography>
                      <Typography variant="caption">
                        Planned: {stop.plannedArrivalTime}
                      </Typography>
                      <br />
                      <Typography variant="caption">
                        Actual: {stop.actualArrivalTime || "--"}
                      </Typography>
                    </Box>
                  </InfoWindow>
                )}
              </Marker>
            );
          })}
        </GoogleMap>

        {/* No Data Overlay */}
      </Card>
    </Box>
  );
};

export default BusLocation;
